import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { userService } from '../../services/userService';
import { authService } from '../../services/authService';
import { committeeService } from '../../services/committeeService';
import { departmentService } from '../../services/departmentService';
import Button from '../ui/Button';
import Table from '../ui/Table';
import ConfirmDialog from '../ui/ConfirmDialog';
import LoadingSpinner from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';
import UniversalFilters from '../common/UniversalFilters';
import { getFilterConfig } from '../../constants/filterConfigs';
import { useFilters } from '../../hooks/useFilters';
import CommitteeManagementModal from '../committee/CommitteeManagementModal';
import NotificationButton from '../notifications/NotificationButton';
import AddUserModal from '../admin/AddUserModal';
import ApproveUserModal from '../admin/ApproveUserModal';
import EditUserModal from '../admin/EditUserModal';
import {
  USER_STATUS,
  USER_STATUS_OPTIONS,
  USER_ROLES,
  ROLE_LABELS,
  SECTORS,
  USER_ACTIONS,
  getSectorLabel,
  PERMISSIONS
} from '../../constants/authConstants';
import { dateUtils, stringUtils, arrayUtils } from '../../utils/helpers';

const UserManagement = () => {
  const { user: currentUser, getUserRole, hasPermission } = useAuth();
  const { isMobile, isTablet, isDesktop, getResponsiveValue } = useTheme();
  
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  const {
    filters,
    handleFilterChange,
    clearFilters,
    hasActiveFilters
  } = useFilters({
    search: '',
    status: '',
    sector: '',
    role: '',
    sort: 'createdAt-desc'
  });

  const filterConfig = getFilterConfig('USER_MANAGEMENT');
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showDropdown, setShowDropdown] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showCommitteeModal, setShowCommitteeModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Check permissions
  const canManageUsers = hasPermission(PERMISSIONS.USER_MANAGEMENT_ACTIONS);
  const canCreateUsers = hasPermission(PERMISSIONS.USER_CREATION);
  const isPlanningOfficial = getUserRole() === USER_ROLES.PLANNING_OFFICIAL;
  const isDoner = getUserRole() === USER_ROLES.DONER;
  // Roles that view user management without any management actions
  const isReadOnlyViewer = isPlanningOfficial || isDoner;

  const headerConfig = getResponsiveValue({
    mobile: { direction: 'col', spacing: 'space-y-4', align: 'stretch' },
    desktop: { direction: 'row', spacing: 'space-x-3', align: 'center' }
  });

  const cardSpacing = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  const titleSize = getResponsiveValue({
    mobile: 'text-xl',
    desktop: 'text-2xl'
  });

  const buttonSize = getResponsiveValue({
    mobile: 'sm',
    desktop: 'md'
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [users, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [allUsers, allCommittees, allDepartments] = await Promise.all([
        userService.getAllUsers(),
        committeeService.getAllCommittees(),
        departmentService.getAllDepartments()
      ]);
      
      setUsers(allUsers);
      setCommittees(allCommittees);
      setDepartments(allDepartments);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCommitteeModalClose = async () => {
    setShowCommitteeModal(false);
    try {
      const allCommittees = await committeeService.getAllCommittees();
      setCommittees(allCommittees);
    } catch (error) {
      console.error('Error reloading committees:', error);
    }
  };

  const handleAddUser = async (userData) => {
    try {
      setFormLoading(true);
      
      const result = await authService.adminCreateUser(userData);
      
      setSuccessMessage(`User ${userData.fullName} has been created successfully! A password reset email has been sent.`);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 5000);
      
      await loadData();
      
      console.log('User added successfully:', result);
    } catch (error) {
      console.error('Error adding user:', error);
      throw error;
    } finally {
      setFormLoading(false);
    }
  };

  const getDepartmentLabel = (departmentCode) => {
    if (!departments || departments.length === 0) return departmentCode;
    
    const dept = departments.find(d => d.departmentCode === departmentCode);
    return dept ? dept.departmentName : departmentCode;
  };

  const applyFiltersAndSort = () => {
    let filtered = [...users];

    if (filters.search && filters.search.trim()) {
      const searchFields = ['fullName', 'email', 'sector', 'department'];
      filtered = arrayUtils.filterBySearch(filtered, filters.search, searchFields);
    }

    if (filters.status) {
      filtered = filtered.filter(user => user.status === filters.status);
    }

    if (filters.sector) {
      filtered = filtered.filter(user => user.sector === filters.sector);
    }

    if (filters.role) {
      filtered = filtered.filter(user => user.role === filters.role);
    }

    if (filters.sort) {
      const [sortBy, order] = filters.sort.split('-');
      
      if (sortBy === 'name') {
        filtered = filtered.sort((a, b) => {
          const nameA = a.fullName?.toLowerCase() || '';
          const nameB = b.fullName?.toLowerCase() || '';
          return order === 'desc' ? nameB.localeCompare(nameA) : nameA.localeCompare(nameB);
        });
      } else {
        filtered = arrayUtils.sortBy(filtered, sortBy, order);
      }
    }

    setFilteredUsers(filtered);
  };

  const handleUserAction = (user, action) => {
    setSelectedUser(user);
    setShowDropdown(null);
    
    switch (action) {
      case USER_ACTIONS.APPROVE:
        // Open approval modal instead of direct approval
        setShowApproveModal(true);
        break;
      case USER_ACTIONS.EDIT:
        // Open edit modal for active/inactive/suspended users
        setShowEditModal(true);
        break;
      case USER_ACTIONS.ACTIVATE:
      case USER_ACTIONS.DEACTIVATE:
      case USER_ACTIONS.SUSPEND:
      case USER_ACTIONS.DELETE:
        setPendingAction(action);
        setShowConfirmDialog(true);
        break;
      default:
        break;
    }
  };

  const handleEditUser = async (user, updateData) => {
    try {
      setFormLoading(true);
      const userId = user.uid || user.id;
      
      if (!userId) {
        console.error('No valid user ID found');
        return;
      }
      
      // Update user with new role, sector, department, and status
      await userService.updateUser(userId, {
        role: updateData.role,
        sector: updateData.sector,
        department: updateData.department,
        status: updateData.status,
        updatedAt: new Date(),
        updatedBy: currentUser.uid || currentUser.id
      });
      
      const message = `${user.fullName}'s details have been updated successfully!`;
      setSuccessMessage(message);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 5000);
      
      await loadData();
      setShowEditModal(false);
      setSelectedUser(null);
      
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    } finally {
      setFormLoading(false);
    }
  };

  const handleApproveUser = async (user, approvalData) => {
    try {
      setFormLoading(true);
      const userId = user.uid || user.id;
      
      if (!userId) {
        console.error('No valid user ID found');
        return;
      }
      
      // Update user with new role, sector, department, and status
      await userService.updateUser(userId, {
        role: approvalData.role,
        sector: approvalData.sector,
        department: approvalData.department,
        status: approvalData.status,
        approvedAt: new Date(),
        approvedBy: currentUser.uid || currentUser.id
      });
      
      const message = `${user.fullName} has been approved as ${ROLE_LABELS[approvalData.role]}!`;
      setSuccessMessage(message);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 5000);
      
      await loadData();
      setShowApproveModal(false);
      setSelectedUser(null);
      
    } catch (error) {
      console.error('Error approving user:', error);
      throw error;
    } finally {
      setFormLoading(false);
    }
  };

  const executeUserAction = async () => {
    if (!selectedUser || !pendingAction) return;

    try {
      setFormLoading(true);
      const userId = selectedUser.uid || selectedUser.id;
      
      if (!userId) {
        console.error('No valid user ID found');
        return;
      }
      
      let message = '';
      
      switch (pendingAction) {
        case USER_ACTIONS.ACTIVATE:
          await userService.updateUserStatus(userId, USER_STATUS.ACTIVE);
          message = `${selectedUser.fullName} has been activated!`;
          break;
        case USER_ACTIONS.DEACTIVATE:
          await userService.updateUserStatus(userId, USER_STATUS.INACTIVE);
          message = `${selectedUser.fullName} has been deactivated.`;
          break;
        case USER_ACTIONS.SUSPEND:
          await userService.updateUserStatus(userId, USER_STATUS.SUSPENDED);
          message = `${selectedUser.fullName} has been suspended.`;
          break;
        case USER_ACTIONS.DELETE:
          await userService.deleteUser(userId);
          message = `${selectedUser.fullName} has been deleted.`;
          break;
        default:
          break;
      }
      
      setSuccessMessage(message);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 5000);
      
      await loadData();
      setShowConfirmDialog(false);
      setPendingAction(null);
      setSelectedUser(null);
      
    } catch (error) {
      console.error('Error executing user action:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const getUserActions = (user) => {
    const actions = [];
    const userId = user.uid || user.id;
    const currentUserId = currentUser?.uid || currentUser?.id;
    
    switch (user.status) {
      case USER_STATUS.PENDING:
        actions.push({
          label: 'Approve',
          action: USER_ACTIONS.APPROVE,
          icon: 'check',
          color: 'text-success hover:text-success-hover'
        });
        break;
      case USER_STATUS.ACTIVE:
        actions.push({
          label: 'Edit',
          action: USER_ACTIONS.EDIT,
          icon: 'edit',
          color: 'text-info hover:text-info-hover'
        });
        actions.push({
          label: 'Deactivate',
          action: USER_ACTIONS.DEACTIVATE,
          icon: 'ban',
          color: 'text-warning hover:text-warning-hover'
        });
        actions.push({
          label: 'Suspend',
          action: USER_ACTIONS.SUSPEND,
          icon: 'pause',
          color: 'text-warning hover:text-warning-hover'
        });
        break;
      case USER_STATUS.INACTIVE:
        actions.push({
          label: 'Edit',
          action: USER_ACTIONS.EDIT,
          icon: 'edit',
          color: 'text-info hover:text-info-hover'
        });
        actions.push({
          label: 'Activate',
          action: USER_ACTIONS.ACTIVATE,
          icon: 'play',
          color: 'text-success hover:text-success-hover'
        });
        break;
      case USER_STATUS.SUSPENDED:
        actions.push({
          label: 'Edit',
          action: USER_ACTIONS.EDIT,
          icon: 'edit',
          color: 'text-info hover:text-info-hover'
        });
        actions.push({
          label: 'Activate',
          action: USER_ACTIONS.ACTIVATE,
          icon: 'play',
          color: 'text-success hover:text-success-hover'
        });
        break;
      default:
        break;
    }

    if (userId !== currentUserId) {
      actions.push({
        label: 'Delete',
        action: USER_ACTIONS.DELETE,
        icon: 'trash',
        color: 'text-error hover:text-error-hover'
      });
    }

    return actions;
  };

  const getStatusBadge = (status) => {
    const statusConfig = USER_STATUS_OPTIONS.find(s => s.value === status);
    if (!statusConfig) return null;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
        {statusConfig.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const themeColors = {
      [USER_ROLES.ADMIN]: 'bg-purple-100 text-purple-800',
      [USER_ROLES.PLANNING_OFFICIAL]: 'bg-indigo-100 text-indigo-800',
      [USER_ROLES.DONER]: 'bg-teal-100 text-teal-800',
      [USER_ROLES.DEPARTMENT]: 'bg-blue-100 text-blue-800'
    };
    
    const themeColor = themeColors[role] || 'bg-info-subtle text-info';
    const roleLabel = ROLE_LABELS[role] || role;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${themeColor}`}>
        {roleLabel}
      </span>
    );
  };

  const getProfilePicture = (user) => {
    if (user.profilePicture) {
      return (
        <img 
          src={user.profilePicture} 
          alt={user.fullName}
          className="w-10 h-10 rounded-xl object-cover border border-border-primary"
        />
      );
    }

    return (
      <div className="w-10 h-10 bg-navigation rounded-xl flex items-center justify-center">
        <span className="text-navigation-text text-sm font-medium">
          {stringUtils.getInitials(user.fullName)}
        </span>
      </div>
    );
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setShowDropdown(null);
    };

    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDropdown]);

  const pendingUsersCount = users.filter(u => u.status === USER_STATUS.PENDING).length;

  // Define columns - conditionally include Actions column
  const baseColumns = [
    {
      key: 'avatar',
      label: '',
      sortable: false,
      render: (value, user) => getProfilePicture(user),
      className: isMobile ? 'hidden' : ''
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (value, user) => (
        <div>
          <div className="font-medium text-text-primary">
            {user.fullName}
          </div>
          <div className="text-sm text-text-secondary">{user.email}</div>
          {!isMobile && (
            <div className="text-xs text-accent">
              {getSectorLabel(user.sector)}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'department',
      label: 'Department',
      sortable: true,
      render: (department) => (
        <div className="text-sm text-text-secondary">
          {getDepartmentLabel(department)}
        </div>
      ),
      className: isMobile ? 'hidden' : ''
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      render: (role) => getRoleBadge(role),
      className: isMobile ? 'hidden' : ''
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (status) => getStatusBadge(status)
    },
    {
      key: 'createdAt',
      label: 'Registered',
      sortable: true,
      render: (date) => (
        <div className="text-sm text-text-secondary">
          {dateUtils.formatDate(date)}
        </div>
      ),
      className: isMobile ? 'hidden' : ''
    }
  ];

  // Actions column - only for users with USER_MANAGEMENT_ACTIONS permission
  const actionsColumn = {
    key: 'actions',
    label: 'Actions',
    sortable: false,
    render: (value, user) => {
      const actions = getUserActions(user);
      const userId = user.uid || user.id;
      
      return (
        <div className="flex space-x-2">
          {actions.slice(0, isMobile ? 1 : 2).map((action, index) => (
            <button
              key={index}
              onClick={() => handleUserAction(user, action.action)}
              className={`p-1 rounded-lg hover:bg-surface-hover transition-colors ${action.color}`}
              title={action.label}
            >
              {getActionIcon(action.icon)}
            </button>
          ))}
          
          {actions.length > (isMobile ? 1 : 2) && (
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDropdown(showDropdown === userId ? null : userId);
                }}
                className="p-1 rounded-lg hover:bg-surface-hover transition-colors text-text-tertiary hover:text-text-primary"
                title="More actions"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>
              
              {showDropdown === userId && (
                <div className="absolute right-0 top-8 w-48 bg-surface-elevated rounded-xl shadow-theme-lg border border-border-primary py-1 z-50">
                  {actions.slice(isMobile ? 1 : 2).map((action, index) => (
                    <button
                      key={index}
                      onClick={() => handleUserAction(user, action.action)}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-surface-hover transition-colors flex items-center space-x-2 ${action.color}`}
                    >
                      <span className="w-4 h-4 flex-shrink-0">
                        {getActionIcon(action.icon)}
                      </span>
                      <span>{action.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
  };

  // Conditionally add Actions column only if user has permission
  const columns = canManageUsers 
    ? [...baseColumns, actionsColumn]
    : baseColumns.filter(col => !col.className?.includes('hidden'));

  const getActionIcon = (iconName) => {
    const icons = {
      check: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>,
      edit: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
      ban: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636a9 9 0 00-12.728 12.728" /></svg>,
      play: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293H15" /></svg>,
      pause: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
      trash: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
    };
    return icons[iconName] || icons.check;
  };

  const getConfirmationMessage = () => {
    if (!selectedUser || !pendingAction) return '';

    const userName = selectedUser.fullName;
    
    switch (pendingAction) {
      case USER_ACTIONS.APPROVE:
        return `Are you sure you want to approve ${userName}'s account? They will be able to login after approval.`;
      case USER_ACTIONS.ACTIVATE:
        return `Are you sure you want to activate ${userName}'s account? They will be able to login after activation.`;
      case USER_ACTIONS.DEACTIVATE:
        return `Are you sure you want to deactivate ${userName}'s account? They will not be able to login until reactivated.`;
      case USER_ACTIONS.SUSPEND:
        return `Are you sure you want to suspend ${userName}'s account? This is a temporary action that can be reversed.`;
      case USER_ACTIONS.DELETE:
        return `Are you sure you want to permanently delete ${userName}'s account? This action cannot be undone.`;
      default:
        return 'Are you sure you want to perform this action?';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading users and data..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showSuccessMessage && (
        <div className="bg-success-subtle border border-border-primary rounded-xl p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-success font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      <div className={`flex ${headerConfig.direction} ${headerConfig.spacing} ${headerConfig.align === 'center' ? 'items-center justify-between' : 'items-start'}`}>
        <div>
          <h1 className={`${titleSize} font-bold text-text-primary`}>User Management</h1>
          <p className="text-text-secondary">
            {isReadOnlyViewer 
              ? 'View department user accounts and approvals' 
              : 'Manage department user accounts and approvals'
            }
          </p>
          {pendingUsersCount > 0 && (
            <div className="mt-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-warning-subtle text-warning">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {pendingUsersCount} user{pendingUsersCount > 1 ? 's' : ''} awaiting approval
              </span>
            </div>
          )}
        </div>
        
        <div className="flex space-x-3">
          <UniversalFilters
            config={filterConfig}
            values={filters}
            onChange={handleFilterChange}
            onClearFilters={clearFilters}
            mode="dropdown"
            dropdownProps={{
              buttonProps: { size: buttonSize },
              contentProps: { layout: 'grid' }
            }}
          />
          
          {/* Add User Button - Only for users with USER_CREATION permission */}
          {canCreateUsers && (
            <Button
              onClick={() => setShowAddUserModal(true)}
              variant="primary"
              size={buttonSize}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add User
            </Button>
          )}
          
          {/* Send Notification - Visible for both Admin and Planning Official */}
          <NotificationButton 
            selectedUsers={filteredUsers} 
            allUsers={users}
            committees={committees}
          />
          
          {/* Manage Committees - Visible for both Admin and Planning Official */}
          <Button
            onClick={() => setShowCommitteeModal(true)}
            variant="outline"
            size={buttonSize}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.121M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.196-2.121M7 20v-2c0-.656.126-1.283.356-1.857M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            Manage Committees
          </Button>
        </div>
      </div>

      <div className="bg-surface-elevated rounded-xl border border-border-primary shadow-theme-sm">
        {filteredUsers.length === 0 ? (
          <div className={cardSpacing}>
            <EmptyState
              icon={
                <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
              }
              title={hasActiveFilters ? "No users found" : "No users registered yet"}
              description={
                hasActiveFilters
                  ? "Try adjusting your search or filter criteria"
                  : "Users will appear here once they register for accounts"
              }
              actionLabel={hasActiveFilters ? "Clear Filters" : null}
              onAction={hasActiveFilters ? clearFilters : null}
            />
          </div>
        ) : (
          <Table
            columns={columns}
            data={filteredUsers}
            emptyMessage="No users found"
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          setPendingAction(null);
          setSelectedUser(null);
        }}
        onConfirm={executeUserAction}
        title={`Confirm ${pendingAction?.replace('_', ' ').replace(/([A-Z])/g, ' $1').toLowerCase().replace(/^\w/, c => c.toUpperCase())}`}
        message={getConfirmationMessage()}
        confirmText={pendingAction === USER_ACTIONS.DELETE ? 'Delete User' : 'Confirm Action'}
        cancelText="Cancel"
        type={pendingAction === USER_ACTIONS.DELETE ? 'danger' : 'info'}
        loading={formLoading}
      />

      <CommitteeManagementModal
        isOpen={showCommitteeModal}
        onClose={handleCommitteeModalClose}
      />

      {canCreateUsers && (
        <AddUserModal
          isOpen={showAddUserModal}
          onClose={() => setShowAddUserModal(false)}
          onUserAdded={handleAddUser}
        />
      )}

      <ApproveUserModal
        isOpen={showApproveModal}
        onClose={() => {
          setShowApproveModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onApprove={handleApproveUser}
      />

      <EditUserModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onUpdate={handleEditUser}
      />
    </div>
  );
};

export default UserManagement;